/**
 * SenderContextPanel — fixed right sidebar showing contextual info about the
 * email sender when they can be identified as a Person or Subscriber in the system.
 *
 * Renders nothing (returns null) when:
 *   - conversationId is null / undefined
 *   - API returns { found: false }
 *   - Data is still loading for the first time and no prior data exists
 */

import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { t } from './support-i18n.js'

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('hu-HU', {
      year: 'numeric', month: 'short', day: 'numeric'
    })
  } catch {
    return iso
  }
}

function formatCurrency(amount, currency) {
  if (amount == null) return '—'
  const cur = currency || 'HUF'
  try {
    return new Intl.NumberFormat('hu-HU', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(amount)
  } catch {
    return `${amount} ${cur}`
  }
}

// Role labels — hungarian first, fallback to raw value
function roleLabel(role) {
  const ROLES = {
    superadmin: 'Szuperadmin',
    admin: 'Admin',
    colleague_pro: 'Kolléga Pro',
    colleague: 'Kolléga',
    guard: 'Portás',
    normal: 'Normál',
    szb_pro: 'SZB Pro',
    szb: 'SZB',
    janitor_pro: 'Gondnok Pro',
    janitor: 'Gondnok',
    gatekeeper: 'Kapuőr',
    cleaner: 'Takarító',
    gardener: 'Kertész'
  }
  return ROLES[role] || role
}

function statusLabel(status) {
  const MAP = {
    open: t('support.sender_context.open_threads.open'),
    inprogress: t('support.sender_context.open_threads.inprogress'),
    waiting_for_customer: t('support.sender_context.open_threads.waiting')
  }
  return MAP[status] || status
}

// ── Skeleton loader ──────────────────────────────────────────────────────────

function ContextSkeleton() {
  return (
    <div class="sc-section">
      <div class="sc-skeleton sc-skeleton-line w-75" />
      <div class="sc-skeleton sc-skeleton-line w-50 mt-2" />
      <div class="sc-skeleton sc-skeleton-line w-60 mt-2" />
    </div>
  )
}

// ── Section wrapper ──────────────────────────────────────────────────────────

function Section({ icon, title, children, badge }) {
  return (
    <div class="sc-section">
      <div class="sc-section-header">
        <i class={`bi ${icon} sc-section-icon`} />
        <span class="sc-section-title">{title}</span>
        {badge != null && <span class="sc-badge">{badge}</span>}
      </div>
      <div class="sc-section-body">
        {children}
      </div>
    </div>
  )
}

// ── Row ──────────────────────────────────────────────────────────────────────

function Row({ label, value, href }) {
  if (!value && value !== 0) return null
  return (
    <div class="sc-row">
      <span class="sc-row-label">{label}</span>
      {href
        ? <a href={href} target="_blank" rel="noopener noreferrer" class="sc-row-value sc-link">{value}</a>
        : <span class="sc-row-value">{value}</span>
      }
    </div>
  )
}

// ── Sections ─────────────────────────────────────────────────────────────────

function PersonSection({ person }) {
  if (!person) return null
  const initial  = ((person.name || person.email || '?').trim()[0] || '?').toUpperCase()
  const gravatarUrl = person.gravatar_url || person.avatar_url

  return (
    <Section icon="bi-person-circle" title={t('support.sender_context.person.title')}>
      <div class="sc-person-header">
        <div class="sc-person-avatar">
          {gravatarUrl
            ? <img src={gravatarUrl} alt={person.name} class="sc-avatar-img" loading="lazy" />
            : <span>{initial}</span>
          }
        </div>
        <div class="sc-person-info">
          <div class="sc-person-name">{person.name}</div>
          <div class="sc-person-email">{person.email}</div>
        </div>
      </div>
      {person.phone_number && (
        <Row
          label={t('support.sender_context.person.phone')}
          value={person.phone_number}
        />
      )}
      {person.role && (
        <Row
          label={t('support.sender_context.person.role')}
          value={roleLabel(person.role)}
        />
      )}
    </Section>
  )
}

function TenantSection({ tenant }) {
  if (!tenant) return null
  return (
    <Section icon="bi-building" title={t('support.sender_context.tenant.title')}>
      <div class="sc-tenant-name">{tenant.name}</div>
      <div class="sc-flags">
        {tenant.email_enabled && (
          <span class="sc-flag sc-flag-on">
            <i class="bi bi-envelope-check" />
            {t('support.sender_context.tenant.email_enabled')}
          </span>
        )}
        {tenant.voice_dictation_enabled && (
          <span class="sc-flag sc-flag-on">
            <i class="bi bi-mic" />
            {t('support.sender_context.tenant.voice_dictation')}
          </span>
        )}
        {tenant.voice_assistant_enabled && (
          <span class="sc-flag sc-flag-on">
            <i class="bi bi-broadcast" />
            {t('support.sender_context.tenant.voice_assistant')}
          </span>
        )}
        {tenant.call_center_enabled && (
          <span class="sc-flag sc-flag-on">
            <i class="bi bi-telephone-inbound" />
            {t('support.sender_context.tenant.call_center')}
          </span>
        )}
      </div>
    </Section>
  )
}

function SubscriberSection({ subscriber }) {
  if (!subscriber) return null
  const intervalMap = {
    monthly:   t('support.sender_context.subscriber.intervals.monthly'),
    quarterly: t('support.sender_context.subscriber.intervals.quarterly'),
    annually:  t('support.sender_context.subscriber.intervals.annually')
  }
  const statusLabel = t(`support.sender_context.subscriber.statuses.${subscriber.status}`, { defaultValue: subscriber.status })

  const hasPipedrive = subscriber.pipedrive_org_url || subscriber.pipedrive_deal_url

  return (
    <Section icon="bi-receipt" title={t('support.sender_context.subscriber.title')}>
      <Row
        label={t('support.sender_context.subscriber.status')}
        value={statusLabel}
      />
      {subscriber.billing_interval && (
        <Row
          label={t('support.sender_context.subscriber.billing_interval')}
          value={intervalMap[subscriber.billing_interval] || subscriber.billing_interval}
        />
      )}
      {subscriber.unit_price != null && (
        <Row
          label={t('support.sender_context.subscriber.unit_price')}
          value={formatCurrency(subscriber.unit_price, subscriber.unit_price_currency || 'HUF')}
        />
      )}
      {subscriber.active_at && (
        <Row
          label={t('support.sender_context.subscriber.active_since')}
          value={formatDate(subscriber.active_at)}
        />
      )}
      {subscriber.trial_ends_at && (
        <Row
          label={t('support.sender_context.subscriber.trial_ends_at')}
          value={formatDate(subscriber.trial_ends_at)}
        />
      )}

      {/* Pipedrive links */}
      {hasPipedrive && (
        <div class="sc-pipedrive-links">
          {subscriber.pipedrive_org_url && (
            <a
              href={subscriber.pipedrive_org_url}
              target="_blank"
              rel="noopener noreferrer"
              class="sc-pipedrive-link"
              title={t('support.sender_context.subscriber.pipedrive.org_link')}
            >
              <img src="https://www.pipedrive.com/favicon.ico" alt="Pipedrive" class="sc-pipedrive-favicon" loading="lazy" />
              {t('support.sender_context.subscriber.pipedrive.org_link')}
            </a>
          )}
          {subscriber.pipedrive_deal_url && (
            <a
              href={subscriber.pipedrive_deal_url}
              target="_blank"
              rel="noopener noreferrer"
              class="sc-pipedrive-link"
              title={t('support.sender_context.subscriber.pipedrive.deal_link')}
            >
              <i class="bi bi-briefcase" />
              {t('support.sender_context.subscriber.pipedrive.deal_link')}
            </a>
          )}
        </div>
      )}

      {/* Active pipeline badge */}
      {subscriber.pipedrive_pipeline_open && subscriber.pipedrive_stage && (
        <div class="sc-pipeline-badge">
          <i class="bi bi-funnel" />
          <span>{t('support.sender_context.subscriber.pipedrive.pipeline_open', { stage: subscriber.pipedrive_stage })}</span>
        </div>
      )}
    </Section>
  )
}

function BuildingComplexesSection({ bcs, count }) {
  if (!bcs) return null
  const extraCount = count - bcs.length

  function bcRoleLabel(role) {
    return t(`support.sender_context.building_complexes.roles.${role}`, { defaultValue: role })
  }
  return (
    <Section
      icon="bi-houses"
      title={t('support.sender_context.building_complexes.title')}
      badge={count > 0 ? count : null}
    >
      {bcs.length === 0
        ? <div class="sc-empty">{t('support.sender_context.building_complexes.empty')}</div>
        : (
          <ul class="sc-bc-list">
            {bcs.map(bc => (
              <li key={bc.id} class="sc-bc-item">
                <i class="bi bi-building sc-bc-icon" />
                <span class="sc-bc-name">{bc.name}</span>
                {bc.role && <span class="sc-bc-role">{bcRoleLabel(bc.role)}</span>}
              </li>
            ))}
            {extraCount > 0 && (
              <li class="sc-bc-more">
                {t('support.sender_context.building_complexes.more', { count: extraCount })}
              </li>
            )}
          </ul>
        )
      }
    </Section>
  )
}

function UnpaidInvoicesSection({ invoices, count }) {
  if (!invoices) return null
  return (
    <Section
      icon="bi-exclamation-triangle"
      title={t('support.sender_context.unpaid_invoices.title')}
      badge={count > 0 ? count : null}
    >
      {invoices.length === 0
        ? <div class="sc-empty">{t('support.sender_context.unpaid_invoices.empty')}</div>
        : (
          <ul class="sc-invoice-list">
            {invoices.map(inv => (
              <li key={inv.id} class="sc-invoice-item">
                <div class="sc-invoice-period">
                  {t('support.sender_context.unpaid_invoices.period', {
                    start: inv.billing_period_start ? inv.billing_period_start.slice(0, 7) : '?',
                    end:   inv.billing_period_end   ? inv.billing_period_end.slice(0, 7)   : '?'
                  })}
                </div>
                <div class="sc-invoice-row">
                  <span class={`sc-invoice-status sc-status-${inv.status}`}>{inv.status}</span>
                  <span class="sc-invoice-amount">{formatCurrency(inv.total_amount, inv.currency)}</span>
                  {inv.public_url && (
                    <a
                      href={inv.public_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="sc-invoice-link"
                      title={t('support.sender_context.unpaid_invoices.view')}
                    >
                      <i class="bi bi-box-arrow-up-right" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )
      }
    </Section>
  )
}

function OpenThreadsSection({ threads, count, onSelectThread }) {
  if (!threads) return null
  return (
    <Section
      icon="bi-chat-left-text"
      title={t('support.sender_context.open_threads.title')}
      badge={count > 0 ? count : null}
    >
      {threads.length === 0
        ? <div class="sc-empty">{t('support.sender_context.open_threads.empty')}</div>
        : (
          <ul class="sc-thread-list">
            {threads.map(thread => (
              <li key={thread.id} class="sc-thread-item">
                <button
                  type="button"
                  class="sc-thread-btn"
                  onClick={() => onSelectThread?.(thread.id)}
                >
                  <span class="sc-thread-subject">{thread.subject}</span>
                  <span class={`sc-status-dot sc-status-${thread.status}`} title={statusLabel(thread.status)} />
                </button>
              </li>
            ))}
          </ul>
        )
      }
    </Section>
  )
}

function AiUsageSection({ usage }) {
  if (!usage) return null
  return (
    <Section icon="bi-stars" title={t('support.sender_context.ai_usage.title')}>
      <Row
        label={t('support.sender_context.ai_usage.conversations')}
        value={usage.conversations_count}
      />
      <Row
        label={t('support.sender_context.ai_usage.messages')}
        value={usage.messages_count}
      />
      <Row
        label={t('support.sender_context.ai_usage.cost_usd')}
        value={`$${usage.total_cost_usd}`}
      />
      {usage.last_used_at && (
        <Row
          label={t('support.sender_context.ai_usage.last_used')}
          value={formatDate(usage.last_used_at)}
        />
      )}
    </Section>
  )
}

// ── Root component ────────────────────────────────────────────────────────────

export function SenderContextPanel({ conversationId, mobileDrawerOpen = false, onCloseMobileDrawer, onSelectThread }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(false)
  const abortRef              = useRef(null)

  useEffect(() => {
    if (!conversationId) {
      setData(null)
      return
    }

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)

    fetch(`/admin/support/conversations/${conversationId}/sender_context.json`, {
      signal: controller.signal,
      headers: { 'Accept': 'application/json' }
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(json => {
        setData(json)
        setLoading(false)
      })
      .catch(err => {
        if (err.name === 'AbortError') return
        setData(null)
        setLoading(false)
      })

    return () => controller.abort()
  }, [conversationId])

  // Don't occupy layout space when sender is not identifiable and drawer is closed
  if (!loading && (!data || !data.found) && !mobileDrawerOpen) return null

  const panelContent = (
    <div class="sc-inner">
      {loading && !data ? (
        <Fragment>
          <ContextSkeleton />
          <ContextSkeleton />
        </Fragment>
      ) : data && data.found ? (
        <Fragment>
          <PersonSection            person={data?.person} />
          <TenantSection            tenant={data?.tenant} />
          <SubscriberSection        subscriber={data?.subscriber} />
          <UnpaidInvoicesSection    invoices={data?.unpaid_invoices}  count={data?.unpaid_invoices_count     ?? 0} />
          <OpenThreadsSection       threads={data?.open_support_threads} count={data?.open_support_threads_count ?? 0} onSelectThread={onSelectThread} />
          <AiUsageSection           usage={data?.ai_usage} />
          <BuildingComplexesSection bcs={data?.building_complexes}   count={data?.building_complexes_count   ?? 0} />
        </Fragment>
      ) : (
        <div class="sc-section">
          <p class="sc-empty-hint">Nem azonosítható feladó</p>
        </div>
      )}
    </div>
  )

  return (
    <Fragment>
      {/* Desktop: fixed right sidebar (hidden on mobile via CSS) */}
      {(!loading && data && data.found) && (
        <div class="sender-context-panel">
          {panelContent}
        </div>
      )}

      {/* Mobile: bottom drawer overlay */}
      {mobileDrawerOpen && (
        <div class="sc-drawer-overlay d-md-none" onClick={onCloseMobileDrawer}>
          <div class="sc-drawer" onClick={e => e.stopPropagation()}>
            <div class="sc-drawer-handle" />
            <div class="sc-drawer-header">
              <span class="sc-drawer-title">Feladó adatai</span>
              <button
                type="button"
                class="sc-drawer-close"
                onClick={onCloseMobileDrawer}
                aria-label="Bezárás"
              >
                <i class="bi bi-x-lg" />
              </button>
            </div>
            <div class="sc-drawer-body">
              {panelContent}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  )
}
